import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { Loader2, Download, Printer, Save, User, Phone, Heart, Sun, Bug, AlertTriangle, Stethoscope, FileText, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import API from '@/config/api';
// API imported from config;

const DCFHealthHistoryForm = ({ open, onClose, childId, childName, token }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  
  const [formData, setFormData] = useState({
    child_last_name: '',
    child_first_name: '',
    child_middle_initial: '',
    child_birthdate: '',
    first_day_attendance: '',
    home_address: '',
    parent_guardian_1: { name: '', primary_phone: '', work_phone: '', secondary_phone: '' },
    parent_guardian_2: { name: '', primary_phone: '', work_phone: '', secondary_phone: '' },
    physician: { name: '', facility_address: '', phone: '' },
    sunscreen: { authorize_apply: null, brand_name: '', ingredient_strength: '', authorize_self_apply: null },
    repellent: { authorize_apply: null, brand_name: '', ingredient_strength: '', authorize_self_apply: null },
    conditions: {
      no_specific_condition: false,
      disorder_cognitive_ld_adhd_autism: false,
      asthma: false,
      cerebral_palsy_motor: false,
      diabetes: false,
      epilepsy_seizure: false,
      gastrointestinal_feeding: false,
      other_condition: false,
      other_condition_specify: '',
      milk_allergy: false,
      food_allergies: false,
      food_allergies_specify: '',
      non_food_allergies: false,
      non_food_allergies_specify: ''
    },
    care_plan: {
      triggers: '',
      signs_symptoms: '',
      provider_steps: '',
      trained_staff_a: '',
      trained_staff_b: '',
      trained_staff_c: '',
      when_to_call_parents: '',
      when_emergency_care: '',
      additional_info: ''
    },
    parent_signature: '',
    signature_date: '',
    review_dates: []
  });

  useEffect(() => {
    if (open && childId) {
      loadData();
    }
  }, [open, childId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/children/${childId}/dcf-health-history-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setFormData(prev => ({
          ...prev,
          ...response.data,
          parent_guardian_1: { ...prev.parent_guardian_1, ...response.data.parent_guardian_1 },
          parent_guardian_2: { ...prev.parent_guardian_2, ...response.data.parent_guardian_2 },
          physician: { ...prev.physician, ...response.data.physician },
          sunscreen: { ...prev.sunscreen, ...response.data.sunscreen },
          repellent: { ...prev.repellent, ...response.data.repellent },
          conditions: { ...prev.conditions, ...response.data.conditions },
          care_plan: { ...prev.care_plan, ...response.data.care_plan },
          review_dates: response.data.review_dates || []
        }));
      }
    } catch (error) {
      console.error('Error loading health history data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/children/${childId}/dcf-health-history-data`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Health history saved successfully');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save health history');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(`${API}/children/${childId}/forms/dcf-2345`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DCF-F-CFS2345_Health_History_${childName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handlePrint = async () => {
    try {
      const response = await axios.get(`${API}/children/${childId}/forms/dcf-2345`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url);
      if (printWindow) {
        printWindow.onload = () => printWindow.print();
      }
    } catch (error) {
      toast.error('Failed to open for printing');
    }
  };

  const updateSection = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: { ...prev[section], [field]: value }
    }));
  };

  const updateCondition = (field, value) => {
    setFormData(prev => ({
      ...prev,
      conditions: { ...prev.conditions, [field]: value }
    }));
  };

  const updateCarePlan = (field, value) => {
    setFormData(prev => ({
      ...prev,
      care_plan: { ...prev.care_plan, [field]: value }
    }));
  };

  const addReviewDate = () => {
    const today = new Date().toISOString().split('T')[0];
    setFormData(prev => ({
      ...prev,
      review_dates: [...(prev.review_dates || []), today]
    }));
  };

  const removeReviewDate = (index) => {
    setFormData(prev => ({
      ...prev,
      review_dates: prev.review_dates.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          <div className="flex items-center justify-center p-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-2">Loading health history...</span>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dcf-health-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Heart className="w-5 h-5 text-red-500" />
            DCF Health History and Emergency Care Plan (DCF-F-CFS2345-E)
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Official Wisconsin DCF health history - {childName}
          </p>
        </DialogHeader>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-health-history-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Form
          </Button>
          <Button variant="outline" onClick={handleDownload} className="gap-2" data-testid="download-health-history-btn">
            <Download className="w-4 h-4" />
            Download PDF
          </Button>
          <Button variant="outline" onClick={handlePrint} className="gap-2">
            <Printer className="w-4 h-4" />
            Print
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="info" data-testid="tab-info">
              <User className="w-4 h-4 mr-1" />
              Info
            </TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">
              <Phone className="w-4 h-4 mr-1" />
              Contacts
            </TabsTrigger>
            <TabsTrigger value="sunscreen" data-testid="tab-sunscreen">
              <Sun className="w-4 h-4 mr-1" />
              Authorizations
            </TabsTrigger>
            <TabsTrigger value="conditions" data-testid="tab-conditions">
              <AlertTriangle className="w-4 h-4 mr-1" />
              Health
            </TabsTrigger>
            <TabsTrigger value="care" data-testid="tab-care">
              <Stethoscope className="w-4 h-4 mr-1" />
              Care Plan
            </TabsTrigger>
          </TabsList>

          {/* INFO TAB */}
          <TabsContent value="info" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="w-4 h-4 text-primary" />
                  Child Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Last Name *</Label>
                    <Input value={formData.child_last_name} onChange={(e) => setFormData({ ...formData, child_last_name: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>First Name *</Label>
                    <Input value={formData.child_first_name} onChange={(e) => setFormData({ ...formData, child_first_name: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>MI</Label>
                    <Input value={formData.child_middle_initial} onChange={(e) => setFormData({ ...formData, child_middle_initial: e.target.value })} maxLength={1} className="w-16" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Birthdate *</Label>
                    <Input type="date" value={formData.child_birthdate} onChange={(e) => setFormData({ ...formData, child_birthdate: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>First Day of Attendance</Label>
                    <Input type="date" value={formData.first_day_attendance} onChange={(e) => setFormData({ ...formData, first_day_attendance: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Home Address (Street, City, State, Zip)</Label>
                  <Input value={formData.home_address} onChange={(e) => setFormData({ ...formData, home_address: e.target.value })} placeholder="123 Main St, Madison, WI 53703" />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CONTACTS TAB */}
          <TabsContent value="contacts" className="space-y-4 mt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <strong>Instructions:</strong> Provide information where the parent(s) / guardian(s) may be reached while the child is in care.
            </div>

            {/* Parent/Guardian 1 */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="w-4 h-4 text-primary" />
                  Parent / Guardian 1
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input value={formData.parent_guardian_1.name} onChange={(e) => updateSection('parent_guardian_1', 'name', e.target.value)} />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Primary Phone</Label>
                    <Input value={formData.parent_guardian_1.primary_phone} onChange={(e) => updateSection('parent_guardian_1', 'primary_phone', e.target.value)} placeholder="(555) 123-4567" />
                  </div>
                  <div className="space-y-2">
                    <Label>Work Phone</Label>
                    <Input value={formData.parent_guardian_1.work_phone} onChange={(e) => updateSection('parent_guardian_1', 'work_phone', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Secondary Phone</Label>
                    <Input value={formData.parent_guardian_1.secondary_phone} onChange={(e) => updateSection('parent_guardian_1', 'secondary_phone', e.target.value)} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Parent/Guardian 2 */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  Parent / Guardian 2 (Optional)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input value={formData.parent_guardian_2.name} onChange={(e) => updateSection('parent_guardian_2', 'name', e.target.value)} />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Primary Phone</Label>
                    <Input value={formData.parent_guardian_2.primary_phone} onChange={(e) => updateSection('parent_guardian_2', 'primary_phone', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Work Phone</Label>
                    <Input value={formData.parent_guardian_2.work_phone} onChange={(e) => updateSection('parent_guardian_2', 'work_phone', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Secondary Phone</Label>
                    <Input value={formData.parent_guardian_2.secondary_phone} onChange={(e) => updateSection('parent_guardian_2', 'secondary_phone', e.target.value)} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Physician */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Stethoscope className="w-4 h-4 text-blue-500" />
                  Physician / Medical Facility
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Physician Name</Label>
                    <Input value={formData.physician.name} onChange={(e) => updateSection('physician', 'name', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input value={formData.physician.phone} onChange={(e) => updateSection('physician', 'phone', e.target.value)} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Medical Facility Address</Label>
                  <Input value={formData.physician.facility_address} onChange={(e) => updateSection('physician', 'facility_address', e.target.value)} />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SUNSCREEN / REPELLENT TAB */}
          <TabsContent value="sunscreen" className="space-y-4 mt-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <strong>Note:</strong> If provided by the parent, the sunscreen or insect repellent shall be labeled with the child's name.
              Per DCF 251.07(6)(g)3., authorizations shall be reviewed every 6 months and updated as necessary.
            </div>

            {/* Sunscreen */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sun className="w-4 h-4 text-yellow-500" />
                  Sunscreen Authorization
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 border rounded-lg bg-gray-50">
                  <div className="space-y-3">
                    <Label className="font-medium">I authorize the center to apply sunscreen to my child:</Label>
                    <RadioGroup
                      value={formData.sunscreen.authorize_apply === true ? 'yes' : formData.sunscreen.authorize_apply === false ? 'no' : ''}
                      onValueChange={(v) => updateSection('sunscreen', 'authorize_apply', v === 'yes')}
                      className="flex gap-4"
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="yes" id="sun-apply-yes" />
                        <Label htmlFor="sun-apply-yes">Yes</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="no" id="sun-apply-no" />
                        <Label htmlFor="sun-apply-no">No</Label>
                      </div>
                    </RadioGroup>
                    {formData.sunscreen.authorize_apply && (
                      <div className="grid grid-cols-2 gap-4 mt-3 p-3 bg-white rounded border">
                        <div className="space-y-2">
                          <Label>Brand Name</Label>
                          <Input value={formData.sunscreen.brand_name} onChange={(e) => updateSection('sunscreen', 'brand_name', e.target.value)} />
                        </div>
                        <div className="space-y-2">
                          <Label>Ingredient Strength (SPF)</Label>
                          <Input value={formData.sunscreen.ingredient_strength} onChange={(e) => updateSection('sunscreen', 'ingredient_strength', e.target.value)} placeholder="e.g., SPF 50" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="p-4 border rounded-lg bg-gray-50">
                  <Label className="font-medium">I authorize the center to allow my child to self-apply sunscreen:</Label>
                  <RadioGroup
                    value={formData.sunscreen.authorize_self_apply === true ? 'yes' : formData.sunscreen.authorize_self_apply === false ? 'no' : ''}
                    onValueChange={(v) => updateSection('sunscreen', 'authorize_self_apply', v === 'yes')}
                    className="flex gap-4 mt-2"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="yes" id="sun-self-yes" />
                      <Label htmlFor="sun-self-yes">Yes</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="no" id="sun-self-no" />
                      <Label htmlFor="sun-self-no">No</Label>
                    </div>
                  </RadioGroup>
                </div>
              </CardContent>
            </Card>

            {/* Insect Repellent */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Bug className="w-4 h-4 text-green-600" />
                  Insect Repellent Authorization
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 border rounded-lg bg-gray-50">
                  <div className="space-y-3">
                    <Label className="font-medium">I authorize the center to apply insect repellent to my child:</Label>
                    <RadioGroup
                      value={formData.repellent.authorize_apply === true ? 'yes' : formData.repellent.authorize_apply === false ? 'no' : ''}
                      onValueChange={(v) => updateSection('repellent', 'authorize_apply', v === 'yes')}
                      className="flex gap-4"
                    >
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="yes" id="rep-apply-yes" />
                        <Label htmlFor="rep-apply-yes">Yes</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <RadioGroupItem value="no" id="rep-apply-no" />
                        <Label htmlFor="rep-apply-no">No</Label>
                      </div>
                    </RadioGroup>
                    {formData.repellent.authorize_apply && (
                      <div className="grid grid-cols-2 gap-4 mt-3 p-3 bg-white rounded border">
                        <div className="space-y-2">
                          <Label>Brand Name</Label>
                          <Input value={formData.repellent.brand_name} onChange={(e) => updateSection('repellent', 'brand_name', e.target.value)} />
                        </div>
                        <div className="space-y-2">
                          <Label>Ingredient Strength (DEET %)</Label>
                          <Input value={formData.repellent.ingredient_strength} onChange={(e) => updateSection('repellent', 'ingredient_strength', e.target.value)} placeholder="e.g., 10% DEET" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="p-4 border rounded-lg bg-gray-50">
                  <Label className="font-medium">I authorize the center to allow my child to self-apply insect repellent:</Label>
                  <RadioGroup
                    value={formData.repellent.authorize_self_apply === true ? 'yes' : formData.repellent.authorize_self_apply === false ? 'no' : ''}
                    onValueChange={(v) => updateSection('repellent', 'authorize_self_apply', v === 'yes')}
                    className="flex gap-4 mt-2"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="yes" id="rep-self-yes" />
                      <Label htmlFor="rep-self-yes">Yes</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="no" id="rep-self-no" />
                      <Label htmlFor="rep-self-no">No</Label>
                    </div>
                  </RadioGroup>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* HEALTH CONDITIONS TAB */}
          <TabsContent value="conditions" className="space-y-4 mt-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
              <strong>Important:</strong> If available, attach any health care plan information from the child's physician, therapist, etc.
            </div>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  1. Check any special medical condition that your child may have:
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50">
                  <Checkbox checked={formData.conditions.no_specific_condition} onCheckedChange={(v) => updateCondition('no_specific_condition', v)} />
                  <span>No specific medical condition</span>
                </label>
                <Separator />
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50">
                  <Checkbox checked={formData.conditions.disorder_cognitive_ld_adhd_autism} onCheckedChange={(v) => updateCondition('disorder_cognitive_ld_adhd_autism', v)} />
                  <span>Any disorder, including Cognitively Disabled, LD, ADD, ADHD, or Autism</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50">
                  <Checkbox checked={formData.conditions.asthma} onCheckedChange={(v) => updateCondition('asthma', v)} />
                  <span>Asthma</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50">
                  <Checkbox checked={formData.conditions.cerebral_palsy_motor} onCheckedChange={(v) => updateCondition('cerebral_palsy_motor', v)} />
                  <span>Cerebral palsy / motor disorder</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50">
                  <Checkbox checked={formData.conditions.diabetes} onCheckedChange={(v) => updateCondition('diabetes', v)} />
                  <span>Diabetes</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50">
                  <Checkbox checked={formData.conditions.epilepsy_seizure} onCheckedChange={(v) => updateCondition('epilepsy_seizure', v)} />
                  <span>Epilepsy / seizure disorder</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50">
                  <Checkbox checked={formData.conditions.gastrointestinal_feeding} onCheckedChange={(v) => updateCondition('gastrointestinal_feeding', v)} />
                  <span>Gastrointestinal or feeding concerns, including special diet and supplements</span>
                </label>
                <div className="p-2 rounded hover:bg-gray-50">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={formData.conditions.other_condition} onCheckedChange={(v) => updateCondition('other_condition', v)} />
                    <span>Other condition(s) requiring special care</span>
                  </label>
                  {formData.conditions.other_condition && (
                    <Input className="mt-2 ml-6" value={formData.conditions.other_condition_specify} onChange={(e) => updateCondition('other_condition_specify', e.target.value)} placeholder="Specify condition(s)" />
                  )}
                </div>
                <Separator />
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50">
                  <Checkbox checked={formData.conditions.milk_allergy} onCheckedChange={(v) => updateCondition('milk_allergy', v)} />
                  <span>Milk allergy (attach statement from medical professional indicating acceptable alternative)</span>
                </label>
                <div className="p-2 rounded hover:bg-gray-50">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={formData.conditions.food_allergies} onCheckedChange={(v) => updateCondition('food_allergies', v)} />
                    <span>Food allergies</span>
                  </label>
                  {formData.conditions.food_allergies && (
                    <Input className="mt-2 ml-6" value={formData.conditions.food_allergies_specify} onChange={(e) => updateCondition('food_allergies_specify', e.target.value)} placeholder="Specify food(s)" />
                  )}
                </div>
                <div className="p-2 rounded hover:bg-gray-50">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox checked={formData.conditions.non_food_allergies} onCheckedChange={(v) => updateCondition('non_food_allergies', v)} />
                    <span>Non-food allergies</span>
                  </label>
                  {formData.conditions.non_food_allergies && (
                    <Input className="mt-2 ml-6" value={formData.conditions.non_food_allergies_specify} onChange={(e) => updateCondition('non_food_allergies_specify', e.target.value)} placeholder="Specify (e.g., bee stings, latex)" />
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* EMERGENCY CARE PLAN TAB */}
          <TabsContent value="care" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Stethoscope className="w-4 h-4 text-red-500" />
                  Emergency Care Plan
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="font-semibold">2. Triggers that may cause problems - Specify:</Label>
                  <Textarea value={formData.care_plan.triggers} onChange={(e) => updateCarePlan('triggers', e.target.value)} rows={2} placeholder="Describe triggers that may cause health problems..." />
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold">3. Signs or symptoms to watch for - Specify:</Label>
                  <Textarea value={formData.care_plan.signs_symptoms} onChange={(e) => updateCarePlan('signs_symptoms', e.target.value)} rows={2} placeholder="Describe signs or symptoms to watch for..." />
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold">4. Steps the child care provider should follow:</Label>
                  <p className="text-xs text-muted-foreground">If prescription or non-prescription medications are necessary, a copy of the form Authorization to Administer Medication should be attached.</p>
                  <Textarea value={formData.care_plan.provider_steps} onChange={(e) => updateCarePlan('provider_steps', e.target.value)} rows={3} placeholder="List specific steps..." />
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold">5. Staff given specialized training/instructions to help treat symptoms:</Label>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs">a.</Label>
                      <Input value={formData.care_plan.trained_staff_a} onChange={(e) => updateCarePlan('trained_staff_a', e.target.value)} placeholder="Staff name" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">b.</Label>
                      <Input value={formData.care_plan.trained_staff_b} onChange={(e) => updateCarePlan('trained_staff_b', e.target.value)} placeholder="Staff name" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">c.</Label>
                      <Input value={formData.care_plan.trained_staff_c} onChange={(e) => updateCarePlan('trained_staff_c', e.target.value)} placeholder="Staff name" />
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold">6. When to call parents regarding symptoms or failure to respond to treatment:</Label>
                  <Textarea value={formData.care_plan.when_to_call_parents} onChange={(e) => updateCarePlan('when_to_call_parents', e.target.value)} rows={2} />
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold">7. When to consider that the condition requires emergency medical care or reassessment:</Label>
                  <Textarea value={formData.care_plan.when_emergency_care} onChange={(e) => updateCarePlan('when_emergency_care', e.target.value)} rows={2} />
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold">8. Additional information that may be helpful to the child care provider:</Label>
                  <Textarea value={formData.care_plan.additional_info} onChange={(e) => updateCarePlan('additional_info', e.target.value)} rows={3} />
                </div>
              </CardContent>
            </Card>

            {/* Signature */}
            <Card className="border-primary">
              <CardHeader className="pb-2 bg-primary/5 rounded-t-lg">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-4 h-4 text-primary" />
                  Parent / Guardian Signature
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Signature (Type Full Name) *</Label>
                    <Input
                      value={formData.parent_signature}
                      onChange={(e) => setFormData({ ...formData, parent_signature: e.target.value })}
                      placeholder="Type your full name"
                      className="font-cursive text-lg"
                      data-testid="parent-signature"
                    />
                    <p className="text-xs text-muted-foreground">
                      By typing your name, you acknowledge this as your electronic signature.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Date Signed *</Label>
                    <Input
                      type="date"
                      value={formData.signature_date}
                      onChange={(e) => setFormData({ ...formData, signature_date: e.target.value })}
                      data-testid="signature-date"
                    />
                  </div>
                </div>
                <Separator />
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="font-semibold">Review Dates:</Label>
                    <Button size="sm" variant="outline" onClick={addReviewDate} className="gap-1">
                      <Plus className="w-3 h-3" />
                      Add Review Date
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Per DCF requirements, this form should be reviewed every 6 months.</p>
                  <div className="flex flex-wrap gap-2">
                    {(formData.review_dates || []).map((date, index) => (
                      <div key={index} className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded">
                        <Input
                          type="date"
                          value={date}
                          onChange={(e) => {
                            const newDates = [...formData.review_dates];
                            newDates[index] = e.target.value;
                            setFormData({ ...formData, review_dates: newDates });
                          }}
                          className="h-7 w-auto border-0 bg-transparent p-0"
                        />
                        <Button size="sm" variant="ghost" onClick={() => removeReviewDate(index)} className="h-6 w-6 p-0">
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-health-history-final-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Health History
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DCFHealthHistoryForm;
